import React, { useState, useEffect } from "react";
import CabIcon from "./CabIcon";
import { getToken } from "../../api/client";

/**
 * Возвращает URL веб-плеера Майнкрафтии с прицеплённым JWT в хэше,
 * чтобы передать авторизацию между доменами без бэкенда.
 * Хэш не уходит в логи серверов (HTTP не шлёт fragment).
 */
function buildMinecraftiaPlayUrl(gameId) {
  const base = `https://minecraftia-school.ru/kubikon/play/${gameId}`;
  const token = getToken();
  if (!token) return base;
  // encodeURIComponent на случай если в JWT есть «+» «/» «=»
  return `${base}#auth=${encodeURIComponent(token)}`;
}

/**
 * Модалка «Запуск игры» — проверяет, установлено ли приложение Рублокса.
 *
 * Браузер не может надёжно определить установленное desktop/Android-приложение.
 * Поэтому используем custom-URL-scheme «rublox://»: пытаемся открыть игру в
 * приложении через deep-link. Если приложение установлено — ОС перехватит
 * переход и страница потеряет фокус (visibilitychange/blur). Если за ~1.5с
 * ничего не произошло — приложения нет, показываем экран «скачать».
 *
 * Это стандартный приём deep-link detection. Без приложения пользователь
 * просто видит предложение скачать — никаких ошибок.
 *
 * props:
 *   game    — {id, title}
 *   onClose — закрыть модалку
 */
export default function PlayCheckModal({ game, onClose }) {
  // phase: "checking" — пробуем открыть в приложении
  //        "notfound" — приложение не обнаружено, предлагаем скачать
  const [phase, setPhase] = useState("checking");

  useEffect(() => {
    if (!game) return;

    let resolved = false;
    let timer = null;

    // если страница потеряла фокус — значит приложение перехватило deep-link
    const onHide = () => {
      if (document.visibilityState === "hidden") resolved = true;
    };
    const onBlur = () => {
      resolved = true;
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("blur", onBlur);

    // пробуем открыть игру в приложении Рублокса
    const deepLink = `rublox://game/${game.id}`;
    try {
      window.location.href = deepLink;
    } catch {
      /* схема не зарегистрирована — браузер бросит ошибку, это ок */
    }

    // через 1.5с проверяем: если фокус не терялся — приложения нет
    timer = setTimeout(() => {
      if (!resolved) setPhase("notfound");
    }, 1500);

    // Esc закрывает
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [game, onClose]);

  if (!game) return null;

  return (
    <div
      className="rb-playmodal__overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="rb-playmodal" role="dialog" aria-modal="true">
        <button
          className="rb-playmodal__close"
          onClick={onClose}
          aria-label="Закрыть"
        >
          <CabIcon name="close" size={16} />
        </button>

        {phase === "checking" ? (
          <div className="rb-playmodal__checking">
            <div className="rb-cab-spinner" />
            <h3 className="rb-playmodal__title">Запускаем игру…</h3>
            <p className="rb-playmodal__text">
              Открываем «{game.title}» в приложении Рублокс.
            </p>
          </div>
        ) : (
          <>
            <div className="rb-playmodal__icon" aria-hidden>
              <CabIcon name="gamepad" size={44} />
            </div>
            <h3 className="rb-playmodal__title">Где запустить игру</h3>
            <p className="rb-playmodal__text">
              На ПК играй в «{game.title}» прямо в браузере на сайте онлайн-школы
              Майнкрафтия — никакой установки не нужно. На телефоне или планшете
              — через приложение Рублокс.
            </p>
            <p className="rb-playmodal__hint">
              Если приложение уже установлено — открой его вручную и найди эту
              игру в каталоге.
            </p>
            <div className="rb-playmodal__actions">
              <a
                href={buildMinecraftiaPlayUrl(game.id)}
                target="_blank"
                rel="noreferrer"
                className="rb-cab-btn rb-cab-btn--grad rb-cab-btn--block"
              >
                Играть в браузере (Майнкрафтия)
              </a>
              <a
                href="/download"
                className="rb-cab-btn rb-cab-btn--ghost rb-cab-btn--block"
              >
                Скачать приложение
              </a>
              <button
                className="rb-cab-btn rb-cab-btn--ghost rb-cab-btn--block"
                onClick={onClose}
              >
                Позже
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
